import type {EntityType} from 'src/types/ats-entities';
import type {Candidate} from 'src/types/crm';
import type {TaskCandidateDetails} from 'src/types/task-and-reminder';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu/Menu';

import * as React from 'react';
import debounce from 'lodash/debounce';
import {useDispatch, useSelector} from 'react-redux';

import {SimpleTypeahead} from '@spaced-out/ui-design-system/lib/components/Typeahead';

import {FALLBACK_CANDIDATE_ENTITY_TYPE} from 'src/constants/job-match-addon';
import {useCrmRedesignedEnabled} from 'src/hooks/product-flags';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {selectCandidateEntityType} from 'src/selectors/ats-entities';
import {selectApplicantInformation} from 'src/selectors/crm';
import * as reduxApi from 'src/utils/redux-api-v2';

import {mergeTypeaheadSelectedOption} from 'src/components/task-and-reminder/lib/merge-typeahead-selected-option';
import {INITIAL_FILTER_STATE} from 'src/components/crm/candidates/constant.ts';


const DEFAULT_CANDIDATE_OPTIONS_LIMIT = 10;

type CandidateEntityRow = {
  id: string;
  full_name?: string;
  external_source_id?: string;
  primary_email?: string;
  primary_phone?: string;
};

type TypeaheadEntitiesResponse = {
  entities?: Array<CandidateEntityRow>;
};

type CandidateOption = {
  id: string;
  secondaryLabelId?: string;
  name: string;
  phone: string;
  email: string;
};

const buildCandidateSecondaryLabel = (candidate: CandidateOption): string =>
  [candidate.phone, candidate.email]
    .filter(
      (value) => value !== null && value !== undefined && value.trim() !== '',
    )
    .map((value) => value.trim())
    .join(' | ');

type TaskCandidateSelectFieldProps = {
  label: string;
  placeholder: string;
  value: TaskCandidateDetails | null;
  onChange: (next: TaskCandidateDetails | null) => void;
  className?: string;
};

const candidateDisplayName = (entity: CandidateEntityRow): string => {
  const fullName =
    typeof entity.full_name === 'string' ? entity.full_name.trim() : '';
  if (fullName) {
    return fullName;
  }
  const email =
    typeof entity.primary_email === 'string' ? entity.primary_email.trim() : '';
  return email;
};

export const TaskCandidateSelectField = ({
  label,
  placeholder,
  value,
  onChange,
  className,
}: TaskCandidateSelectFieldProps) => {
  const dispatch = useDispatch();
  const {t} = useLabelTranslation();
  const isCrmEnabled = useCrmRedesignedEnabled();
  const candidateEntityTypeFromStore = useSelector(selectCandidateEntityType);
  const entityType: EntityType =
    candidateEntityTypeFromStore ?? FALLBACK_CANDIDATE_ENTITY_TYPE;

  const applicantInfo = useSelector(selectApplicantInformation);

  const [entities, setEntities] = React.useState<Array<CandidateOption>>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  // Reuse the candidate already loaded in the store (e.g. the profile panel the
  // task was created from) to show the pre-selected option's email/phone,
  // instead of re-fetching it.
  const selectedCandidate = React.useMemo<CandidateOption | null>(() => {
    const id = value?.id ? String(value.id) : '';
    if (!id || !applicantInfo || String(applicantInfo.id) !== id) {
      return null;
    }
    return {
      id,
      secondaryLabelId: applicantInfo.externalId
        ? String(applicantInfo.externalId)
        : undefined,
      name: applicantInfo.name ?? value?.name ?? '',
      phone: applicantInfo.resume?.phones?.[0] ?? '',
      email: applicantInfo.resume?.emails?.[0] ?? '',
    };
  }, [applicantInfo, value?.id, value?.name]);

  const typeaheadOptions = React.useMemo(
    () =>
      mergeTypeaheadSelectedOption(
        entities.map((entity) => ({
          key: entity.id,
          label: entity.name,
          secondaryLabel: buildCandidateSecondaryLabel(entity),
        })),
        value?.id
          ? {
              id: String(value.id),
              label: value.name ?? '',
              secondaryLabel: selectedCandidate
                ? buildCandidateSecondaryLabel(selectedCandidate)
                : undefined,
            }
          : null,
      ),
    [entities, value?.id, value?.name, selectedCandidate],
  );

  const selectedKeys = React.useMemo(
    () => (value?.id ? [String(value.id)] : []),
    [value?.id],
  );

  const resolveCandidateLabel = React.useCallback(
    (option: MenuOption): React.ReactNode => {
      const candidate =
        entities.find((entity) => entity.id === option.key) ??
        (selectedCandidate?.id === option.key ? selectedCandidate : undefined);
      const idText = (candidate?.secondaryLabelId ?? option.key).trim();
      return (
        <div>
          {option.label}
          {idText ? <span>{` | ${t('ID', 'ID')}: ${idText}`}</span> : null}
        </div>
      );
    },
    [entities, selectedCandidate, t],
  );

  const fetchCandidates = React.useCallback(
    async (query: string): Promise<Array<CandidateOption>> => {
      if (isCrmEnabled) {
        const response = (await dispatch(
          reduxApi.post(
            '/api/candidates/search',
            {...INITIAL_FILTER_STATE, q: query ? [query] : []},
            {
              searchId: '-1',
              type: 'applied',
              page: '0',
              pageSize: String(DEFAULT_CANDIDATE_OPTIONS_LIMIT),
              sortBy: 'OVERALL_SCORE',
            },
            {apiPath: '/trmapi'},
          ),
        )) as {content: Array<{candidate: Candidate}>} | null | undefined;
        const list = response?.content;
        const rows = Array.isArray(list) ? list : [];
        return rows.reduce<Array<CandidateOption>>(
          (acc, {candidate: {id, name, resume}}) => {
            if (id != null) {
              acc.push({
                id: String(id),
                name: name ?? resume?.emails?.[0] ?? '',
                phone: resume?.phones?.[0] ?? '',
                email: resume?.emails?.[0] ?? '',
              });
            }
            return acc;
          },
          [],
        );
      }

      const response = (await dispatch(
        reduxApi.get(`typeahead/entities/${entityType}`, {
          search_string: query,
          limit: DEFAULT_CANDIDATE_OPTIONS_LIMIT,
          offset: 0,
        }),
      )) as TypeaheadEntitiesResponse | null | undefined;
      const list = response?.entities;
      const rows = Array.isArray(list) ? list : [];
      return rows.map((entity) => ({
        id: String(entity.id),
        secondaryLabelId: entity.external_source_id
          ? String(entity.external_source_id)
          : undefined,
        name: candidateDisplayName(entity),
        phone: entity.primary_phone ?? '',
        email: entity.primary_email ?? '',
      }));
    },
    [dispatch, entityType, isCrmEnabled],
  );

  const debouncedSearch = React.useMemo(
    () =>
      debounce((query: string) => {
        void (async () => {
          setIsLoading(true);
          try {
            const list = await fetchCandidates(query);
            setEntities(list);
          } finally {
            setIsLoading(false);
          }
        })();
      }, 400),
    [fetchCandidates],
  );

  React.useEffect(() => {
    void (async () => {
      setIsLoading(true);
      try {
        const list = await fetchCandidates('');
        setEntities(list);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [fetchCandidates]);

  const handleSelect = (option: MenuOption) => {
    const row = entities.find((entity) => entity.id === option.key);
    const name = row ? row.name : option.label || '';
    onChange({id: option.key, name});
  };

  const handleClear = () => {
    onChange(null);
    void (async () => {
      setIsLoading(true);
      try {
        const list = await fetchCandidates('');
        setEntities(list);
      } finally {
        setIsLoading(false);
      }
    })();
  };

  return (
    <div className={className}>
      <SimpleTypeahead
        label={label}
        placeholder={placeholder}
        selectedKeys={selectedKeys}
        menuOpenOffset={0}
        onSearch={(evt) => {
          debouncedSearch(evt.target.value);
        }}
        onSelect={handleSelect}
        onClear={handleClear}
        options={typeaheadOptions}
        resolveLabel={resolveCandidateLabel}
        isLoading={isLoading}
        testId="task-candidate-select"
      />
    </div>
  );
};
